/**
 * The settle fallback's release policy — pure, so the one decision that
 * chooses between "liveness" and "a dead band paints" can be pinned in a
 * DOM-free test.
 *
 * `ScrollHorizontal.whenSettled(debt)` releases truthfully when the bind queue
 * drains with the caller's `SettleDebt` repaid. Its fallback exists
 * for the case where that drain never comes: a superseding rapid switch can
 * replace the queue without a final `onBatchComplete`, which would strand the
 * reveal. So the fallback re-arms while binds are queued or generation debt
 * is outstanding and eventually releases anyway.
 *
 * "Eventually" used to be a fixed count of mutate cycles, blind to whether the
 * debt was being repaid. A switch chased by a second bind (a `goToDate` right
 * behind a `navigateToSheet`) drops the first pass's binds as stale at drain
 * time and re-emits them at the live generation — they are QUEUED, the drain
 * that paints them is frames away — and the count ran out one millisecond
 * after the re-emit: the reveal fired onto the scrubbed 0px cells and the
 * `sheet/reveal-contract` dead-column error followed on every switch
 * (header-month-label-fresh-after-switch, red since 08-28). The queue being
 * non-empty is exactly the evidence that a drain will come; only an EMPTY queue
 * with the debt still owed is the liveness case the count was written for.
 */
export namespace SettleFallback {
  /** Mutate-cycle re-arms allowed while the debt is owed and NOTHING is queued
   *  (the liveness case: nothing will ever bind at the awaited generation). */
  export const IDLE_REARMS = 5;

  /** Wall-clock bound on waiting for QUEUED binds to drain. Generous next to a
   *  drain (a frame or two); it exists so a queue that never drains — a stuck
   *  batcher — still cannot lock navigation. */
  export const PENDING_MAX_MS = 500;

  export interface State {
    /** `SettleDebt.owed` — the awaited bind has not run. The debt's kind, not
     *  this policy, decides which comparison that is. */
    readonly owed: boolean;
    /** `ScrollEngine.hasPendingBinds` — binds are queued, a drain is coming. */
    readonly pending: boolean;
    /** Re-arms taken so far while owed with an EMPTY queue. */
    readonly idleRearms: number;
    /** Wall-clock ms since the waiter was armed. */
    readonly elapsedMs: number;
  }

  export type Decision =
    /** Binds pending or debt owed; wait another cycle. `countsAsIdle` says whether this cycle
     *  spends one of the idle re-arms (empty queue) or rides the pending
     *  budget (queued binds). */
    | { readonly kind: "rearm"; readonly countsAsIdle: boolean }
    /** Debt repaid and queue empty; release on the bounded wait. */
    | { readonly kind: "release"; readonly via: "fallback" }
    /** Binds still pending or debt STILL owed, with the wait budget spent; release anyway —
     *  liveness beating correctness, the one line that says a dead band may
     *  be about to paint. */
    | { readonly kind: "release"; readonly via: "fallback-exhausted" };

  export function decide(state: State): Decision {
    if (state.pending) {
      // Queued binds are the evidence the count never had: the drain that
      // paints them is scheduled. An earlier paint may already have repaid
      // the debt before scrolling queued more cells in the same generation.
      // Wait for the current queue, bounded by wall clock only.
      if (state.elapsedMs < PENDING_MAX_MS) {
        return { kind: "rearm", countsAsIdle: false };
      }
      return { kind: "release", via: "fallback-exhausted" };
    }
    if (!state.owed) return { kind: "release", via: "fallback" };
    if (state.idleRearms < IDLE_REARMS) {
      return { kind: "rearm", countsAsIdle: true };
    }
    return { kind: "release", via: "fallback-exhausted" };
  }
}
